import { ArrowLeft, Edit, UserIcon } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { buildApiUrl } from "../../lib/utils";
import AddRoleModal from "./AddRole";
import { Permission, Role } from "./@types";
import { useGetRoleByIdQuery } from "./userApiSlice";

const RoleDetailsSkeleton = () => (
  <div className="min-h-screen bg-[#F9F9F9] p-4 space-y-6">
    <div className="h-[80px] bg-white rounded-[10px] animate-pulse" />
    <div className="bg-white rounded-[10px] p-6 space-y-6">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className="h-4 w-full max-w-md bg-gray-300 rounded animate-pulse"
        />
      ))}
    </div>
  </div>
);

const formatAction = (action: string) =>
  action
    .split(".")
    .pop()
    ?.replace(/([A-Z])/g, " $1")
    .trim() || action;

const RoleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canUpdate } = usePermissions();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const basePath = pathname.startsWith("/super-admin")
    ? "/super-admin"
    : "/admin";

  const { data, isLoading, isError, refetch } = useGetRoleByIdQuery(id || "", {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  if (isLoading) return <RoleDetailsSkeleton />;

  if (isError || !data?.data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-hColor mb-2">
            Role not found
          </h2>
          <p className="text-pColor mb-4">
            The role you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/85"
          >
            Back to Roles
          </button>
        </div>
      </div>
    );
  }

  const role: Role = data.data;
  const permissions: Permission[] = role.permissions ?? [];
  const assignees = role.assignees ?? [];

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-[10px] p-4 sm:p-6 space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <button
                onClick={() => navigate(-1)}
                className="shrink-0 p-2 -ml-2 rounded-full hover:bg-gray-100 text-hColor"
                aria-label="Back"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-[20px] font-semibold text-hColor break-words">
                    {role.name}
                  </h1>
                  {role.isPro && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                      Pro
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[14px] text-pColor break-words">
                  {role.description || "No description"}
                </p>
              </div>
            </div>

            {canUpdate("Roles") && (
              <button
                onClick={() => setIsEditOpen(true)}
                className="w-full sm:w-auto justify-center text-[13px] font-semibold flex items-center gap-2 px-4 py-2 h-[38px] bg-primary text-white rounded-lg hover:bg-primary/85 transition-colors"
              >
                Edit role
                <Edit size={13} />
              </button>
            )}
          </div>

          <div className="h-px w-full bg-gray-200" />

          <div className="grid grid-cols-2 gap-4 sm:max-w-md">
            <div>
              <p className="text-xs text-pColor mb-1">Permissions</p>
              <p className="text-[20px] font-semibold text-hColor">
                {permissions.length}
              </p>
            </div>
            <div>
              <p className="text-xs text-pColor mb-1">Users with this role</p>
              <p className="text-[20px] font-semibold text-hColor">
                {assignees.length}
              </p>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-white rounded-[10px]">
          <div className="p-4 sm:p-6">
            <h2 className="text-[16px] font-semibold text-hColor">
              Permissions
            </h2>
          </div>
          <div className="border-b border-gray-200 w-full" />
          <div className="p-4 sm:p-6">
            {permissions.length === 0 ? (
              <p className="text-pColor">No permissions assigned.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {permissions.map((perm) => (
                  <div
                    key={perm.resourceName}
                    className="rounded-[10px] border border-gray-200 p-4 space-y-3"
                  >
                    <p className="text-[14px] font-semibold text-hColor break-words">
                      {perm.resourceName}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {perm.actions.map((action) => (
                        <span
                          key={action}
                          className="text-[12px] font-medium px-3 py-1 rounded-full bg-primary/10 text-primary"
                        >
                          {formatAction(action)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Assignees */}
        <div className="bg-white rounded-[10px]">
          <div className="p-4 sm:p-6">
            <h2 className="text-[16px] font-semibold text-hColor">
              Users with this role ({assignees.length})
            </h2>
          </div>
          <div className="border-b border-gray-200 w-full" />
          <div className="p-2 sm:p-4">
            {assignees.length === 0 ? (
              <p className="p-2 text-pColor">
                No users have been assigned this role.
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {assignees.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => navigate(`${basePath}/user/${user.id}`)}
                    className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-gray-50 px-2 rounded-md"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      {user.imageUrl ? (
                        <img
                          src={buildApiUrl(user.imageUrl)}
                          alt={user.fullName}
                          className="w-10 h-10 shrink-0 rounded-full object-cover border border-gray-300"
                        />
                      ) : (
                        <div className="w-10 h-10 shrink-0 rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 p-2">
                          <UserIcon className="w-full h-full text-hColor" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-hColor truncate">
                          {user.fullName || user.userName}
                        </p>
                        <p className="text-[13px] text-pColor truncate">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`shrink-0 text-[12px] font-semibold px-2 py-0.5 rounded-full ${
                        user.isActive
                          ? "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {isEditOpen && (
        <AddRoleModal
          isOpen={isEditOpen}
          mode="edit"
          existingRole={{
            roleId: role.id,
            name: role.name,
            description: role.description,
            permissions,
          }}
          onClose={() => {
            refetch();
            setIsEditOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default RoleDetails;
